// src/annihilation/CharacterCreate.jsx
// ============================================================
//  ANNIHILATION — the Transcendence.
//  Source of truth: ANNIHILATION_BUILD_GUIDE (PRIME) Part 3
//  (The Awakening → Escling → Transcendence), beings.js (the
//  ONE registry — this file hardcodes no being data).
//
//  The player arrives as an Escling and chooses which Beb
//  lineage to Transcend into. The choice is PERMANENT for this
//  character — same law as skill points. A second purchased
//  character can choose differently.
//
//  Save shape (annihilation___character, field-level writes):
//    beingId · xp (level derives — never stored) · equippedLineId
//    · skillTrees (engine save state) · transcendedAt
// ============================================================

import React, { useState } from "react";
import { BEINGS, BEING_IDS } from "./beings";
import { BEING_BASE_STATS } from "./stats";
import { emptyTreeState } from "./treeEngine";
import { saveCharacter } from "./profileWrites";

export default function CharacterCreate({ uid, onCreated, onBack }) {
  const [chosen, setChosen] = useState(null); // beingId being inspected
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const being = chosen ? BEINGS[chosen] : null;

  const transcend = async () => {
    if (!uid || !being || saving) return;
    setSaving(true);
    setError(null);
    try {
      const character = {
        beingId: being.id,
        xp: 0, // level derives via stats.levelForXp — never stored
        equippedLineId: being.lines?.[0]?.id || null,
        skillTrees: emptyTreeState(),
        transcendedAt: Date.now(),
      };
      await saveCharacter(uid, character);
      onCreated?.(character);
    } catch (e) {
      console.warn("[annihilation] transcendence save failed", e);
      setError("The Transcendence faltered — try again.");
      setSaving(false);
    }
  };

  return (
    <div style={S.void}>
      <div style={S.frame}>
        <div style={S.kicker}>The Awakening</div>
        <h1 style={S.title}>Transcendence</h1>
        <p style={S.lede}>
          You woke an Escling — small, unformed, unclaimed. Now the lineages
          call. Choose what you will become.{" "}
          <span style={S.permanent}>This choice is permanent.</span>
        </p>

        {/* ---- the four, from the ONE registry ---- */}
        <div style={S.grid}>
          {BEING_IDS.map((id) => {
            const b = BEINGS[id];
            const active = chosen === id;
            return (
              <button
                key={id}
                style={{ ...S.card, ...(active ? S.cardActive : null) }}
                onClick={() => {
                  setChosen(id);
                  setConfirming(false);
                }}
              >
                <div style={S.cardLineage}>{b.lineage}</div>
                <div style={S.cardIlk}>
                  {b.ilk} · {b.archetype}
                </div>
                <div style={S.cardHook}>{b.oneLiner}</div>
              </button>
            );
          })}
        </div>

        {/* ---- inspection panel ---- */}
        {being ? (
          <div style={S.inspect}>
            <div style={S.epigraph}>“{being.epigraph}”</div>
            <div style={S.statRow}>
              {(() => {
                const s = BEING_BASE_STATS[being.id];
                if (!s) return null;
                return (
                  <>
                    <Stat label="Anima" value={s.anima} color="#e86a5c" />
                    <Stat label="Chao" value={s.chao} color="#6ab4e8" />
                    <Stat label="Lyf" value={s.lyf} color="#8fd18a" />
                    <Stat label="Lyt" value={s.lytCap} color="#e8a33d" />
                  </>
                );
              })()}
            </div>
            <div style={S.lytLine}>
              <span style={S.lytName}>{being.lytAttack.name}</span> —{" "}
              {being.lytAttack.effect}
            </div>

            {!confirming ? (
              <button style={S.transcend} onClick={() => setConfirming(true)}>
                Transcend as {being.lineage}
              </button>
            ) : (
              <div style={S.confirmBox}>
                <div style={S.confirmText}>
                  Become {being.lineage === "Xulan" ? "a" : "a"}{" "}
                  <b>{being.lineage}</b>? There is no way back — only forward,
                  and up.
                </div>
                <div style={S.confirmRow}>
                  <button
                    style={S.confirmYes}
                    disabled={saving}
                    onClick={transcend}
                  >
                    {saving ? "Transcending…" : "I am ready"}
                  </button>
                  <button
                    style={S.confirmNo}
                    disabled={saving}
                    onClick={() => setConfirming(false)}
                  >
                    Not yet
                  </button>
                </div>
              </div>
            )}
            {error ? <div style={S.error}>{error}</div> : null}
          </div>
        ) : (
          <div style={S.hint}>Touch a lineage to look upon it.</div>
        )}

        {onBack ? (
          <button style={S.back} onClick={onBack}>
            ← remain an Escling a while longer
          </button>
        ) : null}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={S.stat}>
      <div style={{ ...S.statValue, color }}>{value}</div>
      <div style={S.statLabel}>{label}</div>
    </div>
  );
}

const S = {
  void: {
    minHeight: "100vh",
    background:
      "radial-gradient(ellipse at 50% 30%, #14101c 0%, #0a0810 55%, #050408 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
    boxSizing: "border-box",
    fontFamily: "Georgia, 'Times New Roman', serif",
  },
  frame: {
    maxWidth: 620,
    width: "100%",
    border: "1px solid rgba(232,163,61,0.35)",
    borderRadius: 12,
    padding: "30px 24px",
    background: "rgba(12,10,18,0.88)",
    boxShadow: "0 0 60px rgba(232,163,61,0.08), inset 0 0 40px rgba(0,0,0,0.5)",
    textAlign: "center",
  },
  kicker: {
    color: "#8a7a5c",
    fontSize: 12,
    letterSpacing: "0.35em",
    textTransform: "uppercase",
    marginBottom: 10,
  },
  title: {
    color: "#e8a33d",
    fontSize: 30,
    margin: "0 0 10px",
    fontWeight: 600,
    textShadow: "0 0 24px rgba(232,163,61,0.35)",
  },
  lede: { color: "#d8cdb8", fontSize: 14.5, lineHeight: 1.6, margin: "0 0 20px" },
  permanent: { color: "#e86a5c", fontStyle: "italic" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
    gap: 10,
    marginBottom: 18,
  },
  card: {
    background: "rgba(232,163,61,0.05)",
    border: "1px solid rgba(232,163,61,0.25)",
    borderRadius: 10,
    padding: "14px 10px",
    cursor: "pointer",
    fontFamily: "inherit",
    textAlign: "center",
    transition: "border-color 0.15s, background 0.15s",
  },
  cardActive: {
    border: "1px solid rgba(232,163,61,0.9)",
    background: "rgba(232,163,61,0.12)",
    boxShadow: "0 0 18px rgba(232,163,61,0.18)",
  },
  cardLineage: { color: "#e8a33d", fontSize: 15, fontWeight: 600, marginBottom: 4 },
  cardIlk: {
    color: "#8a7a5c",
    fontSize: 10.5,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: 6,
  },
  cardHook: { color: "#b5a284", fontSize: 11.5, fontStyle: "italic", lineHeight: 1.4 },
  inspect: {
    border: "1px solid rgba(232,163,61,0.2)",
    borderRadius: 10,
    padding: "16px 14px",
    background: "rgba(0,0,0,0.3)",
    marginBottom: 16,
  },
  epigraph: {
    color: "#9a8d74",
    fontSize: 13,
    fontStyle: "italic",
    lineHeight: 1.55,
    marginBottom: 14,
  },
  statRow: { display: "flex", justifyContent: "center", gap: 22, marginBottom: 14 },
  stat: { textAlign: "center" },
  statValue: { fontSize: 20, fontWeight: 700 },
  statLabel: {
    color: "#6d5f47",
    fontSize: 10,
    letterSpacing: "0.2em",
    textTransform: "uppercase",
    marginTop: 2,
  },
  lytLine: { color: "#d8cdb8", fontSize: 12.5, lineHeight: 1.55, marginBottom: 16 },
  lytName: { color: "#e8a33d", fontWeight: 600 },
  transcend: {
    background: "rgba(232,163,61,0.14)",
    border: "1px solid rgba(232,163,61,0.7)",
    color: "#e8a33d",
    padding: "12px 26px",
    borderRadius: 8,
    fontSize: 15,
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: "0.05em",
  },
  confirmBox: {
    border: "1px solid rgba(232,106,92,0.45)",
    borderRadius: 8,
    padding: "12px 14px",
    background: "rgba(232,106,92,0.06)",
  },
  confirmText: { color: "#d8cdb8", fontSize: 13.5, lineHeight: 1.5, marginBottom: 12 },
  confirmRow: { display: "flex", justifyContent: "center", gap: 10 },
  confirmYes: {
    background: "rgba(232,106,92,0.15)",
    border: "1px solid rgba(232,106,92,0.8)",
    color: "#e86a5c",
    padding: "10px 20px",
    borderRadius: 8,
    fontSize: 14,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  confirmNo: {
    background: "transparent",
    border: "1px solid rgba(232,163,61,0.4)",
    color: "#b5a284",
    padding: "10px 20px",
    borderRadius: 8,
    fontSize: 14,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  error: { color: "#e86a5c", fontSize: 12.5, marginTop: 10 },
  hint: { color: "#6d5f47", fontSize: 12.5, fontStyle: "italic", marginBottom: 16 },
  back: {
    background: "transparent",
    border: "1px solid rgba(232,163,61,0.5)",
    color: "#e8a33d",
    padding: "10px 22px",
    borderRadius: 8,
    fontSize: 13.5,
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: "0.05em",
    marginTop: 4,
  },
};
